import { useLazyQuery } from '@apollo/client/react';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  GetPreparedTelemedicineAppointmentDocument,
  PreparedTelemedicineAppointmentFragment,
} from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';
import { useSubscriptions } from '~/hooks/use-subscriptions';
import { telemedicineAppointmentsState } from '~/state/layouts/SharedAppShell/atoms';

export const useSubscriptionTelemedicineAppointment = () => {
  const [appointmentEvents, setAppointmentEvents] = useState<
    { appointmentId: string; type: string }[]
  >([]);
  const [{ isEnabled, appointments }, setState] = useAtom(telemedicineAppointmentsState);
  const [event] = useSubscriptions('Appointment', ['prepared_telemedicine', 'updated']);

  const [getAppointment, { data }] = useLazyQuery(GetPreparedTelemedicineAppointmentDocument, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });
  const appointmentEventsRef = useRef(appointmentEvents);
  appointmentEventsRef.current = appointmentEvents;
  const processedDataRef = useRef<typeof data>(undefined);

  useEffect(() => {
    if (!data || data === processedDataRef.current) return;
    processedDataRef.current = data;

    const node = getNode(data, 'Appointment');
    if (!node) return;

    const _appointment = node as PreparedTelemedicineAppointmentFragment;

    const currentEvent = appointmentEventsRef.current.find(
      ({ appointmentId }) => appointmentId === _appointment.id,
    );
    setAppointmentEvents((_state) =>
      _state.filter(({ appointmentId }) => appointmentId !== _appointment.id),
    );

    if (currentEvent?.type === 'prepared_telemedicine') {
      setState((_state) => ({
        ..._state,
        appointments: [..._state.appointments, _appointment],
      }));
    }

    if (
      currentEvent?.type === 'updated' &&
      _appointment.status !== 'pending' &&
      _appointment.status !== 'booked'
    ) {
      // 服薬指導が完了したら、準備完了通知を消す
      setState((_state) => ({
        ..._state,
        appointments: _state.appointments.filter(({ id }) => id !== _appointment.id),
      }));
    }
  }, [data, setState]);

  const handleDelete = useCallback(
    (appointmentId: string) => {
      const newAppointments = appointments.filter(({ id }) => id !== appointmentId);
      setState((_state) => ({ ..._state, appointments: newAppointments }));
    },
    [appointments, setState],
  );

  const fetch = useCallback(
    async (id: string) => {
      // FIXME: 連続でクエリ実行すると自動でクエリがcancelされるため、
      // 間隔を開けて実行する
      await new Promise<void>((resolve) => {
        setTimeout(() => {
          getAppointment({
            variables: {
              id,
            },
          });
          resolve();
        }, 500);
      });
    },
    [getAppointment],
  );

  useEffect(() => {
    if (isEnabled && event) {
      setAppointmentEvents((_state) => [
        ..._state,
        { appointmentId: event.typeId, type: event.actionName },
      ]);
    }
  }, [event, getAppointment, isEnabled]);

  useEffect(() => {
    const fetchData = async () => {
      for (const event of appointmentEvents) {
        await fetch(event.appointmentId);
      }
    };
    fetchData();
  }, [appointmentEvents, fetch]);

  return {
    handleDelete,
  };
};
